import { useQueryClient } from '@tanstack/react-query';
import { useFullShopifyProducts } from 'hooks/product/useFullShopifyProducts';
import { useShopifyProducts } from 'hooks/product/useShopifyProducts';
import { useAnalytics } from 'hooks/useAnalytics';
import type { CartUpdateSchema } from 'lib/analytics/schema';
import { useCallback } from 'react';
import type { ProductBundle } from 'types/cms/blocks';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';

import { useAddItems } from './useAddItems';
import { useCartUI } from './useCartUI';
import { useDiscounts } from './useDiscounts';

const getProductByVariantId = (
  products: (ShopifyProduct | undefined)[],
  merchandiseId: string,
) =>
  products.find(
    (product): product is ShopifyProduct =>
      !!product && product.variants.some(({ id }) => id === merchandiseId),
  );

interface UseAddProductBundleOptions extends Pick<ProductBundle, 'products'> {
  discountCodes?: string[];
}

export function useAddProductBundle({
  products,
  discountCodes,
}: UseAddProductBundleOptions) {
  const queryClient = useQueryClient();
  const merchandiseIds = products.map((p) => p.variant.gid);
  const shopifyProducts = useShopifyProducts({ merchandiseIds });
  const handles = shopifyProducts.map((p) => p.handle);
  const { data: fullShopifyProducts } = useFullShopifyProducts(handles);

  const { mutate: addItems, isPending: isAddingItems } =
    useAddItems(queryClient);
  const { addDiscounts, isPending: isAddingDiscount } =
    useDiscounts(queryClient);
  const { toggleCart } = useCartUI();

  const { analytics } = useAnalytics();

  const handleAddItemsToCart = useCallback(() => {
    addItems(
      products.map((p) => ({
        merchandiseId: p.variant.gid,
      })),
      {
        onSuccess: () => {
          if (!discountCodes) return toggleCart(true);
          return addDiscounts(discountCodes, {
            onSettled: () => toggleCart(true),
          });
        },
      },
    );

    const items = merchandiseIds.reduce((acc, merchandiseId) => {
      const fullShopifyProduct = getProductByVariantId(
        fullShopifyProducts,
        merchandiseId,
      );
      if (!fullShopifyProduct) return acc;
      return [
        ...acc,
        {
          product: fullShopifyProduct,
          merchandiseId,
          quantity: 1,
        },
      ];
    }, [] as CartUpdateSchema);

    analytics.addToCart(items);
  }, [
    addItems,
    addDiscounts,
    toggleCart,
    products,
    discountCodes,
    analytics,
    merchandiseIds,
    fullShopifyProducts,
  ]);

  return { handleAddItemsToCart, isAddingItems, isAddingDiscount };
}
